"use client"

import Link from "next/link";
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useParams, usePathname, useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import * as Icons from "@/components/fun-ui/icons";
import { FlameIcon, FoldLeftIcon, FoldRightIcon } from "@/components/fun-ui/icons";
import { useGlobalConfig, useUserVip, useHelpPopup, useResearchDrawing, useSubjects } from "@/stores/appStore"
import { DashboardLogo } from "@/components/Logo";
import { Button, buttonVariants } from "@/components/fun-ui/Button";
import { dashboardConfig } from "@/config/dashboard"
import { CommunicateWithUs } from "@/components/CommunicateWithUs";
import { UserAccountNav } from "@/components/user-account-nav";
import { IconButton } from "@/components/icon-button";
import { SidebarNavItem } from "@/types";
import { Tip } from "../fun-ui/Tip";
import { UsageQuotasForSidebar } from "./UsageQuotas";
import { MarketingEntrance } from "./MarketingEntrance";
import { UpgradeVipButton } from "../payment/UpgradeVipButton";
import { ReceiveVipButton } from "../payment/ReceiveVipButton";
import { useSidebarCollapsed } from "@/stores/localStore";
import { HelpPopup } from "@/components/guides/HelpPopup";
import { DemoMaskedText } from "@/components/demo/DemoMaskedText";
import { CreateOrEditSubjectDialog } from "@/components/subjects/CreateOrEditSubjectDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/fun-ui/Popover";

const SUBJECT_LIST_SCROLL_STORAGE_KEY = "findin:sidebar:subject-list:scrollTop";

export function Sidebar() {
  const path = usePathname();
  const router = useRouter();
  const {
    collapsed,
    setCollapsed
  } = useSidebarCollapsed();
  const {
    vip: isVip,
    isLargePaymentUser,
    vipExtra,
  } = useUserVip();
  const { isOpen: helpPopupIsOpen } = useHelpPopup();
  const { resetFormValue: resetResearchDrawingFormValue } = useResearchDrawing();
  const {
    subjects,
    subjectsFetched,
    fetchSubjectsLoading,
    fetchSubjects,
  } = useSubjects();

  const { paperId } = useParams();

  const [subjectsExpanded, setSubjectsExpanded] = useState<boolean>(false);
  const [collapsedSubjectMenuOpen, setCollapsedSubjectMenuOpen] = useState<boolean>(false);
  const [subjectCreateScrollVersion, setSubjectCreateScrollVersion] = useState<number>(0);
  const subjectListRef = useRef<HTMLDivElement>(null);
  const subjectScrollTopRef = useRef<number>(0);
  const subjectScrollRestoredRef = useRef<boolean>(false);
  const previousPathRef = useRef<string | null>(path);
  const subjectScrollInteractingRef = useRef<boolean>(false);
  const subjectScrollInteractionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollToNewestSubjectRef = useRef<boolean>(false);
  const {
    value: globalConfig,
    fetch: fetchGlobalConfig,
  } = useGlobalConfig().globalConfig;

  const normalizePath = (value?: string) => {
    if (!value) {
      return "";
    }
    return value.split("?")[0].split("#")[0];
  };

  useEffect(() => {
    fetchGlobalConfig();
  }, [fetchGlobalConfig]);

  useEffect(() => {
    if (paperId) {
      setCollapsed(true);
    }
  }, [paperId, setCollapsed]);

  useEffect(() => {
    if (!collapsed && !subjectsFetched) {
      fetchSubjects();
    }
  }, [collapsed, fetchSubjects, subjectsFetched]);

  useEffect(() => {
    if (collapsed && collapsedSubjectMenuOpen && !subjectsFetched) {
      fetchSubjects();
    }
  }, [collapsed, collapsedSubjectMenuOpen, fetchSubjects, subjectsFetched]);

  const sortedSubjects = useMemo(() => {
    return [...subjects].sort(({ updateTime: a }, { updateTime: b }) => {
      const bTime = new Date(b).getTime() || 0;
      const aTime = new Date(a).getTime() || 0;
      return bTime - aTime;
    });
  }, [subjects]);

  const activeSubjectId = normalizePath(path).match(/^\/subjects\/([^/]+)/)?.[1] ?? "";

  const saveSubjectScrollTop = useCallback((scrollTop: number) => {
    subjectScrollTopRef.current = scrollTop;
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(SUBJECT_LIST_SCROLL_STORAGE_KEY, String(scrollTop));
    }
  }, []);

  const markSubjectScrollInteraction = useCallback(() => {
    subjectScrollInteractingRef.current = true;
    if (subjectScrollInteractionTimerRef.current) {
      clearTimeout(subjectScrollInteractionTimerRef.current);
    }
    subjectScrollInteractionTimerRef.current = setTimeout(() => {
      subjectScrollInteractingRef.current = false;
      subjectScrollInteractionTimerRef.current = null;
    }, 500);
  }, []);

  const handleSubjectCreated = useCallback((subjectId?: string) => {
    setSubjectsExpanded(true);
    scrollToNewestSubjectRef.current = true;
    setSubjectCreateScrollVersion((version) => version + 1);
    if (subjectId) {
      router.push(`/subjects/${subjectId}`);
    }
  }, [router]);

  useEffect(() => {
    return () => {
      if (subjectScrollInteractionTimerRef.current) {
        clearTimeout(subjectScrollInteractionTimerRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    const list = subjectListRef.current;
    if (!subjectsExpanded || !list || collapsed) {
      return;
    }
    const pathChanged = previousPathRef.current !== path;
    previousPathRef.current = path;
    if (scrollToNewestSubjectRef.current) {
      scrollToNewestSubjectRef.current = false;
      subjectScrollRestoredRef.current = true;
      saveSubjectScrollTop(0);
      list.scrollTop = 0;
      return;
    }
    if ((subjectScrollRestoredRef.current || subjectScrollInteractingRef.current) && !pathChanged) {
      return;
    }
    subjectScrollRestoredRef.current = true;
    const maxScrollTop = Math.max(list.scrollHeight - list.clientHeight, 0);
    const storedScrollTop = typeof window !== "undefined"
      ? Number(window.sessionStorage.getItem(SUBJECT_LIST_SCROLL_STORAGE_KEY))
      : 0;
    const lastScrollTop = Number.isFinite(storedScrollTop) ? storedScrollTop : subjectScrollTopRef.current;
    if (lastScrollTop <= 0) {
      return;
    }
    const nextScrollTop = Math.min(lastScrollTop, maxScrollTop);
    subjectScrollTopRef.current = nextScrollTop;
    if (Math.abs(list.scrollTop - nextScrollTop) > 1) {
      list.scrollTop = nextScrollTop;
    }
  }, [path, saveSubjectScrollTop, subjectCreateScrollVersion, subjectsExpanded, collapsed]);

  useLayoutEffect(() => {
    const list = subjectListRef.current;
    if (!subjectsExpanded || !list || !activeSubjectId || subjectScrollInteractingRef.current || collapsed) {
      return;
    }
    const activeLink = Array.from(list.querySelectorAll<HTMLElement>("[data-sidebar-subject-id]"))
      .find((element) => element.dataset.sidebarSubjectId === activeSubjectId);
    if (!activeLink) {
      return;
    }
    const listRect = list.getBoundingClientRect();
    const activeRect = activeLink.getBoundingClientRect();
    let nextScrollTop = list.scrollTop;

    if (activeRect.top < listRect.top) {
      nextScrollTop -= listRect.top - activeRect.top;
    } else if (activeRect.bottom > listRect.bottom) {
      nextScrollTop += activeRect.bottom - listRect.bottom;
    } else {
      return;
    }

    nextScrollTop = Math.max(
      0,
      Math.min(nextScrollTop, Math.max(list.scrollHeight - list.clientHeight, 0)),
    );
    if (Math.abs(list.scrollTop - nextScrollTop) > 1) {
      list.scrollTop = nextScrollTop;
      saveSubjectScrollTop(nextScrollTop);
    }
  }, [activeSubjectId, saveSubjectScrollTop, subjectsExpanded, sortedSubjects, collapsed, subjectScrollInteractingRef]);

  const { sidebarNav: items, bottomNav: bottomItems, userAccountNav } = dashboardConfig;
  const middleBottomItems = bottomItems.filter((item) => item.component !== "Divider");
  const fixedBottomDividerItems = bottomItems.filter((item) => item.component === "Divider");
  const helpCenterRef = useRef<HTMLDivElement>(null);

  const isExternalUrl = (value?: string) => value?.startsWith("http://") || value?.startsWith("https://");

  const isSidebarItemActive = (item: SidebarNavItem) => {
    const currentPath = normalizePath(path);
    const candidates = [item.href, ...(item.activeHrefs ?? [])].filter(Boolean) as string[];

    return candidates.some((candidate) => {
      if (isExternalUrl(candidate)) {
        return false;
      }

      const candidatePath = normalizePath(candidate);
      if (!candidatePath) {
        return false;
      }

      if (candidatePath === "/") {
        return currentPath === "/";
      }

      return currentPath === candidatePath || currentPath.startsWith(`${candidatePath}/`);
    });
  };

  const SidebarItem = (item: SidebarNavItem) => {
    const Icon = item.icon ? Icons[item.icon] : null
    const isExternalLink = isExternalUrl(item.href);
    const Component = item.component && SidebarComponents[item.component] || (isExternalLink ? 'a' : Link);
    const linkProps = isExternalLink && item.target ? { target: item.target, rel: 'noopener noreferrer' } : (item.target ? { target: item.target, rel: 'noopener noreferrer' } : {});
    // 判断是否为"帮助中心"且 HelpPopup 已打开
    const isHelpCenter = item.title === "帮助中心";
    const isActive = isSidebarItemActive(item);
    const isHelpPopupHover = isHelpCenter && helpPopupIsOpen;
    const shouldMaskDemoText = item.href === "/summarizes";
    const hasSubjectChildren = sortedSubjects.length > 0;
    const shouldRenderSubjectList = subjectsExpanded && (
      (fetchSubjectsLoading && !subjectsFetched) || subjectsFetched || hasSubjectChildren
    );

    const closeCollapsedSubjectMenu = () => {
      setCollapsedSubjectMenuOpen(false);
    };

    const renderSubjectList = (isPopover = false) => (
      <div
        ref={isPopover ? null : subjectListRef}
        className={cn(
          "overflow-y-auto overscroll-contain [scrollbar-color:#d1d5db_transparent] [scrollbar-width:thin]",
          "[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#d1d5db] [&::-webkit-scrollbar-thumb:hover]:bg-[#9ca3af]",
          isPopover ? "w-full pr-1" : "ml-8 mt-2",
          "max-h-[212px]",
        )}
        onPointerDown={markSubjectScrollInteraction}
        onScroll={(evt) => {
          markSubjectScrollInteraction();
          saveSubjectScrollTop(evt.currentTarget.scrollTop);
        }}
        onWheel={(evt) => {
          markSubjectScrollInteraction();
          const list = evt.currentTarget;
          const maxScrollTop = list.scrollHeight - list.clientHeight;
          const isAtTop = list.scrollTop <= 0;
          const isAtBottom = list.scrollTop >= maxScrollTop - 1;
          if ((evt.deltaY < 0 && isAtTop) || (evt.deltaY > 0 && isAtBottom)) {
            evt.preventDefault();
          }
          evt.stopPropagation();
        }}
      >
        {
          fetchSubjectsLoading && !subjectsFetched ? (
            <div className="px-3 py-2 text-xs text-annotation-foreground">加载中...</div>
          ) : sortedSubjects.length ? (
            <div className="space-y-2">
              {sortedSubjects.map(({ id, subjectName }) => {
                const subjectActive = activeSubjectId === id;
                return (
                  <div
                    key={id}
                    className="py-0"
                  >
                    <Link
                      href={`/subjects/${id}`}
                      scroll={false}
                      title={subjectName}
                      data-sidebar-subject-id={id}
                      onClick={() => {
                        if (subjectListRef.current) {
                          saveSubjectScrollTop(subjectListRef.current.scrollTop);
                        }
                        if (isPopover) {
                          closeCollapsedSubjectMenu();
                        }
                      }}
                      className={cn(
                        "flex h-10 items-center rounded-lg pl-3 pr-2 text-sm text-muted-foreground hover:bg-muted",
                        subjectActive && "bg-muted text-primary",
                      )}
                    >
                      <span className="truncate">{subjectName}</span>
                    </Link>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-3 py-2 text-xs text-annotation-foreground">暂无主题分类</div>
          )
        }
      </div>
    );

    const subjectActionBar = (
      <div className="ml-auto flex shrink-0 items-center gap-0">
        <Tip label="创建主题分类" side="top" sideOffset={8} truncate={false} disableFocusTrigger>
          <span className="inline-flex">
            <CreateOrEditSubjectDialog onSuccess={handleSubjectCreated}>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="创建主题分类"
                className="h-8 w-6 shrink-0 text-muted-foreground hover:bg-transparent hover:text-primary"
                onClick={(evt) => {
                  evt.stopPropagation();
                }}
              >
                <Icons.PlusIcon size={18} />
              </Button>
            </CreateOrEditSubjectDialog>
          </span>
        </Tip>
        {!collapsed ? (
          <Tip label={subjectsExpanded ? "收起" : "展开"} side="top" sideOffset={8} truncate={false}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={subjectsExpanded ? "收起主题分类" : "展开主题分类"}
              aria-expanded={subjectsExpanded}
              className={cn(
                "h-8 w-6 shrink-0 text-muted-foreground hover:bg-transparent hover:text-primary",
              )}
              onClick={(evt) => {
                evt.preventDefault();
                evt.stopPropagation();
                setSubjectsExpanded((value) => !value);
              }}
            >
              <Icons.ChevronDownIcon
                size={18}
                className={cn(
                  "transition-transform",
                  subjectsExpanded && "rotate-180",
                )}
              />
            </Button>
          </Tip>
        ) : null}
      </div>
    );

    if (item.href === "/subjects") {
      if (collapsed) {
        return (
          <Popover
            open={collapsedSubjectMenuOpen}
            onOpenChange={setCollapsedSubjectMenuOpen}
          >
            <div className="relative mx-1">
              <PopoverTrigger asChild openOnClick={false}>
                <button
                  type="button"
                  className={cn(
                    "group flex h-10 w-full items-center justify-center rounded-lg text-muted-foreground hover:bg-muted",
                    isActive && "bg-muted text-primary hover:bg-muted",
                    item.disabled && "cursor-not-allowed opacity-80",
                  )}
                  aria-label={item.title}
                  onClick={() => {
                    if (!item.disabled) {
                      closeCollapsedSubjectMenu();
                      router.push("/subjects");
                    }
                  }}
                >
                  {
                    Icon ? (
                      <span
                        className={cn(
                          "flex h-6 w-6 items-center justify-center rounded-md",
                          isActive && "text-primary",
                        )}
                      >
                        <Icon size={item.size ?? 16} />
                      </span>
                    ) : null
                  }
                </button>
              </PopoverTrigger>
            </div>
            <PopoverContent
              side="right"
              align="start"
              alignOffset={-8}
              sideOffset={12}
              className="w-[165px] rounded-lg border bg-background p-2 shadow-sm"
              onOpenAutoFocus={(evt) => evt.preventDefault()}
              onCloseAutoFocus={(evt) => evt.preventDefault()}
            >
              <div
                className="flex h-10 items-center gap-3 rounded-lg pl-3 pr-1 text-sm text-muted-foreground"
              >
                {
                  Icon ? (
                    <span className="flex h-8 w-4 shrink-0 items-center justify-center">
                      <Icon size={item.size ?? 16} />
                    </span>
                  ) : null
                }
                <span className="flex h-8 min-w-0 flex-1 items-center truncate whitespace-nowrap leading-none">{item.title}</span>
                <Tip label="创建主题分类" side="top" sideOffset={8} truncate={false} disableFocusTrigger>
                  <span className="inline-flex">
                    <CreateOrEditSubjectDialog
                      onSuccess={(subjectId) => {
                        closeCollapsedSubjectMenu();
                        handleSubjectCreated(subjectId);
                      }}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="创建主题分类"
                        className="h-8 w-7 shrink-0 items-center justify-center p-0 text-muted-foreground hover:bg-transparent hover:text-primary"
                        onClick={(evt) => {
                          evt.stopPropagation();
                        }}
                      >
                        <Icons.PlusIcon size={18} />
                      </Button>
                    </CreateOrEditSubjectDialog>
                  </span>
                </Tip>
              </div>
              <div className="mt-1 border-t border-border pt-1">
                {renderSubjectList(true)}
              </div>
            </PopoverContent>
          </Popover>
        );
      }

      return (
        <div className="relative mx-2">
          <div
            ref={isHelpCenter ? helpCenterRef : null}
            className={cn(
              "flex h-10 items-center pl-4 pr-2 text-sm bg-background text-muted-foreground rounded-lg hover:bg-muted overflow-hidden",
              isActive && "bg-muted text-primary",
              item.disabled && "cursor-not-allowed opacity-80",
            )}
          >
            <Link
              href={!item.disabled && item.href ? item.href : ''}
              className="flex min-w-0 flex-1 items-center gap-3"
            >
              {
                Icon ?
                  <span className="min-w-4 min-h-4 flex justify-center"><Icon size={item.size ?? 16} /></span> : null
              }
              <span className="min-w-0 truncate whitespace-nowrap">{item.title}</span>
            </Link>
            {subjectActionBar}
          </div>
          {shouldRenderSubjectList ? renderSubjectList() : null}
        </div>
      );
    }

    return (
      <Component
        href={!item.disabled && item.href ? item.href : ''}
        className={cn(
          "relative group",
          collapsed ? "mx-1 flex h-10 items-center justify-center rounded-lg hover:bg-muted" : "mx-2",
          collapsed && isActive && "bg-muted text-primary hover:bg-muted",
        )}
        onClick={() => {
          if (item.href === "/research-drawing") {
            resetResearchDrawingFormValue();
            if (typeof window !== "undefined") {
              window.dispatchEvent(new Event("research-drawing:reset"));
            }
          }
        }}
        {...linkProps}
      >
        {
          collapsed ? (
            <div
              ref={isHelpCenter ? helpCenterRef : null}
              className="relative flex h-10 items-center justify-center"
            >
              <div className="relative inline-flex">
                {
                  (
                    Icon ? (item.href ?
                      <IconButton
                        className={cn(
                          "bg-transparent hover:bg-transparent",
                          isActive ? "text-primary" : "",
                        )}
                        label={item.title} side="right" Icon={Icon}
                      /> : (
                        <div
                          className={cn(
                            "h-6 w-6",
                            buttonVariants({
                              variant: "ghost",
                              size: "icon",
                            })
                          )}
                        >
                          <Icon size={item.size ?? 16} />
                        </div>
                      )) : null
                  )
                }
                {item.badge ? (
                  <span
                    className="absolute right-[1px] top-[1px] z-30 h-[5px] w-[5px] rounded-full bg-[#ff4d4f] shadow-[0_0_0_2px_var(--background)]"
                  />
                ) : null}
              </div>
            </div>
          ) : (
            <div
              ref={isHelpCenter ? helpCenterRef : null}
              className={cn(
                "flex items-center gap-3 pl-4 pr-2 py-2.5 text-sm bg-background text-muted-foreground rounded-lg hover:bg-muted overflow-hidden",
                isActive && "bg-muted text-primary",
                isHelpPopupHover && "bg-muted",
                item.disabled && "cursor-not-allowed opacity-80",
              )}
            >
              {
                Icon ?
                  <span className="min-w-4 min-h-4 flex justify-center"><Icon size={item.size ?? 16} /></span> : null
              }
              {
                shouldMaskDemoText ? (
                  <DemoMaskedText
                    as="div"
                    className="flex min-w-0 flex-1 items-center gap-1"
                  >
                    <span className="min-w-0 shrink whitespace-nowrap">{item.title}</span>
                    {item.badge ? (
                      <span className="inline-flex h-4 shrink-0 items-center whitespace-nowrap rounded-full border border-[#ff5858] bg-background px-1 text-[10px] leading-none text-[#ff5858] shadow-sm">
                        <FlameIcon size={10} />
                        <span className="ml-0.5">{item.badge}</span>
                      </span>
                    ) : null}
                  </DemoMaskedText>
                ) : (
                  <div className="flex min-w-0 flex-1 items-center gap-1">
                    <span className="min-w-0 shrink whitespace-nowrap">{item.title}</span>
                    {item.badge ? (
                      <span className="inline-flex h-4 shrink-0 items-center whitespace-nowrap rounded-full border border-[#ff5858] bg-background px-1 text-[10px] leading-none text-[#ff5858] shadow-sm">
                        <FlameIcon size={10} />
                        <span className="ml-0.5">{item.badge}</span>
                      </span>
                    ) : null}
                  </div>
                )
              }
            </div>
          )
        }
      </Component>
    );
  }
  return (
    <aside
      className={[
        "group/sidebar relative z-40 grid h-full shrink-0 grid-rows-[auto_minmax(0,1fr)_auto] transition-all",
        collapsed ? "duration-100 w-[48px] min-w-[48px] max-w-[48px]"
          : "duration-300 w-[180px] min-w-[180px] max-w-[180px] min-[1280px]:w-[200px] min-[1280px]:min-w-[200px] min-[1280px]:max-w-[200px] min-[1440px]:w-[220px] min-[1440px]:min-w-[220px] min-[1440px]:max-w-[220px]",
      ].join(' ')}
    >
      <div className="flex h-[60px] items-center justify-center w-full flex-shrink-0">
        <Link href="/papers" className="flex items-center justify-center">
          <DashboardLogo iconOnly={collapsed} />
        </Link>
      </div>
      <div className={cn(
        "min-h-0 overflow-y-auto overscroll-contain [scrollbar-color:#d1d5db_transparent] [scrollbar-width:thin]",
        "[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#d1d5db] [&::-webkit-scrollbar-thumb:hover]:bg-[#9ca3af]",
        collapsed ? "overflow-x-visible" : "overflow-x-hidden",
      )}>
        <div className="flex min-h-full flex-col pt-2">
          <nav className="flex flex-col space-y-2 ">
            {items.map((item, index) => (
              <Fragment key={index}>
                {SidebarItem(item)}
              </Fragment>
            ))}
          </nav>
          <nav className="mt-auto flex flex-col space-y-2 pt-2">
            <MarketingEntrance collapsed={collapsed} />
            {middleBottomItems.map((item, index) => (
              <Fragment key={index}>
                {SidebarItem(item)}
              </Fragment>
            ))}
          </nav>
        </div>
      </div>
      <div className="shrink-0 pb-5">
        {fixedBottomDividerItems.map((item, index) => (
          <Fragment key={index}>
            {SidebarItem(item)}
          </Fragment>
        ))}
        <div className={cn(
          "mt-2 flex flex-col space-y-2",
          collapsed ? "mx-1" : "mx-2"
        )}>
          {
            (!isVip && !isLargePaymentUser && vipExtra) ? (
              <Tip label={collapsed ? "升级会员享特惠" : ""} side="right">
                <div className={cn(
                  "flex flex-1 items-center",
                  collapsed ? "justify-center p-2.5" : "px-4 py-2.5",
                )}>
                  <UpgradeVipButton
                    size="sm"
                    className={cn(
                      "flex-1",
                      collapsed ? "h-6 px-1.5" : "pl-1"
                    )}
                    iconSize={collapsed ? 12 : 16}
                    iconOnly={collapsed}
                  >
                    升级会员享特惠
                  </UpgradeVipButton>
                </div>
              </Tip>
            ) : null
          }
          {
            (!isVip && isLargePaymentUser) ? (
              <ReceiveVipButton collapsed={collapsed} />
            ) : null
          }
          <UsageQuotasForSidebar className={cn(
            "flex flex-1 items-center text-sm bg-background text-muted-foreground rounded-lg hover:bg-muted space-x-4",
            collapsed ? "justify-center p-2.5" : "px-4 py-2.5",
          )} iconOnly={collapsed} />
          <UserAccountNav
            className={cn(
              "flex flex-1 items-center p-2 text-sm bg-background text-muted-foreground rounded-lg hover:bg-muted space-x-4",
              collapsed ? "px-1.5" : "px-2"
            )}
            iconOnly={collapsed}
            navItems={userAccountNav}
          />
        </div>
      </div>
      <Tip side="right" sideOffset={8} label={collapsed ? "展开菜单" : "收起菜单"}>
        <div
          onClick={() => {
            setCollapsed(!collapsed);
          }}
          className={[
            "absolute z-10 cursor-pointer right-0 top-10 translate-x-1/2 w-[26px] h-[26px] justify-center items-center rounded-full shadow-[0_3px_8px_0_rgba(0,0,0,0.16)] bg-background flex opacity-0",
            "transition-opacity duration-300",
            "hover:opacity-100 group-hover/sidebar:opacity-100 group-focus-within/sidebar:opacity-100",
          ].join(' ')
          }
        >
          {collapsed ? <FoldRightIcon /> : <FoldLeftIcon />}
        </div>
      </Tip>
      <HelpPopup helpCenterRef={helpCenterRef} />
    </aside>
  );
}

export const SidebarComponents = {
  CommunicateWithUs: ({ children }: { children: React.JSX.Element }) => (
    <CommunicateWithUs
      side="right"
    >
      <div className="mx-2">{children}</div>
    </CommunicateWithUs>
  ),
  Divider: () => (
    <div className="shrink-0 px-2 h-[1px]">
      <hr className="shrink-0 bg-border h-full w-full" />
    </div>
  )
}
